/**
 * The Action Queue's alert cards, built from the reads the Overview already
 * makes. Moved out of the old HomeScreen body unchanged in substance; what
 * changed is noted at each site.
 *
 * A FAILED READ IS SAID FIRST AND SAID OUT LOUD. Without that a dead API left
 * this list empty — the "nothing needs you" screen. Failures are grouped by
 * kind, one card each, because with the API down every read fails at once and
 * seven identical cards bury the sentence.
 *
 * EVERY CARD CARRIES A KEY, AND THE LIST IS DEDUPED ON IT. The old page drew
 * the time-off banner, Action Required, the Morning Brief's finished-job list
 * and the Work Finished queue as four separate regions, and a job a crew had
 * reported done appeared in BOTH of the last two. The Action Queue is one
 * panel: the brief is a one-line recap above it (no job list), and the
 * finished jobs are listed once, by the queue.
 */
import type { OpsAlert } from '../dashboard/OpsAlerts';
import { fmtCurrency, parseMoney } from '../../lib/display-labels';
import { instantTenantDay } from '../../lib/calendar-date';
import { loadFailureMessage, type LoadFailureKind } from '../../lib/load-failure';

export interface KeyedAlert extends OpsAlert {
  key: string;
}

export interface ActionAlertInputs {
  today: string | null;
  timeZone: string | null;
  jobs: any[];
  tickets: any[];
  ticketDash: any;
  equipment: any[];
  equipCerts: any[];
  teamCertSummary: any;
  safetyDash: any;
  invoiceDash: any;
  quoteDash: any;
  /**
   * The currency of quoteDash's pending value: set for an NDT tenant only
   * (lib/ndt-quotes.ts ndtTenantQuoteCurrency). Absent keeps the bare '$'.
   */
  quoteCurrency?: string | null;
  timeOff: any;
  failures: {
    jobs: LoadFailureKind | null;
    tickets: LoadFailureKind | null;
    ticketDash: LoadFailureKind | null;
    equipment: LoadFailureKind | null;
    equipCerts: LoadFailureKind | null;
    teamCerts: LoadFailureKind | null;
    safety: LoadFailureKind | null;
    invoices: LoadFailureKind | null;
    quotes: LoadFailureKind | null;
  };
  /** Wall clock for the "3+ days" ticket age. Injected so a test can pin it. */
  now?: number;
}

/** A count off a summary payload, or null when absent or unparseable. Never `|| 0`. */
export function parseCount(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** "a", "a and b", "a, b and c". */
function joinSubjects(subjects: string[]): string {
  if (subjects.length <= 1) return subjects.join('');
  return `${subjects.slice(0, -1).join(', ')} and ${subjects[subjects.length - 1]}`;
}

const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

export function buildActionAlerts(i: ActionAlertInputs): KeyedAlert[] {
  const alerts: KeyedAlert[] = [];
  const f = i.failures;

  const readFailures: { kind: LoadFailureKind | null; subject: string; action: string; path: string }[] = [
    { kind: f.jobs, subject: 'jobs', action: 'Dispatch', path: '/dispatch' },
    { kind: f.tickets, subject: 'tickets', action: 'Tickets', path: '/tickets' },
    { kind: f.ticketDash, subject: 'customer-rejected tickets', action: 'Tickets', path: '/tickets?status=customer_rejected' },
    { kind: f.equipment, subject: 'equipment', action: 'Equipment', path: '/equipment' },
    { kind: f.equipCerts, subject: 'equipment certs', action: 'Equipment', path: '/equipment/certs' },
    { kind: f.teamCerts, subject: 'team certs', action: 'Safety', path: '/safety/team-certs' },
    { kind: f.safety, subject: 'safety records', action: 'Safety', path: '/safety' },
  ];
  (['unreachable', 'refused'] as const).forEach((kind) => {
    const failed = readFailures.filter((r) => r.kind === kind);
    if (failed.length === 0) return;
    const { heading } = loadFailureMessage(kind, joinSubjects(failed.map((r) => r.subject)));
    alerts.push({
      key: `failure:${kind}`,
      label: heading,
      detail: kind === 'refused'
        ? 'Not a statement about whether anything needs action'
        : 'Not a statement that nothing needs action. Try again in a moment',
      action: failed[0].action,
      path: failed[0].path,
      failure: kind,
    });
  });

  // Time off. This was a full-width banner above the old page; it is one card
  // in the queue now, with the same count and the same link.
  const pendingTimeOff = parseCount(i.timeOff?.pendingCount) ?? 0;
  if (pendingTimeOff > 0) {
    const names = ((i.timeOff.upcoming ?? i.timeOff.thisWeek ?? []) as any[])
      .filter((n) => n.status === 'pending')
      .slice(0, 2)
      .map((n) => `${n.first_name || ''} ${n.last_name || ''}`.trim())
      .filter(Boolean);
    alerts.push({
      key: 'time-off',
      label: `${pendingTimeOff} time-off ${plural(pendingTimeOff, 'request')} pending approval`,
      detail: names.join(', '),
      action: 'Review',
      path: '/time-off',
    });
  }

  // Unstarted jobs scheduled for today or overdue (same logic as the dispatch banner).
  const unstarted = i.jobs.filter((j) => {
    if (j.status !== 'pending' && j.status !== 'scheduled') return false;
    const sd = j.scheduled_date || j.scheduledDate;
    if (!sd || i.today === null) return false;
    const day = typeof sd === 'string' ? sd.split(/[T ]/)[0] : '';
    return !!day && day <= i.today;
  });
  if (unstarted.length > 0) alerts.push({
    key: 'jobs-unstarted',
    label: `${unstarted.length} ${plural(unstarted.length, 'job')} ${unstarted.length === 1 ? 'needs' : 'need'} to be started`,
    detail: unstarted.slice(0, 2).map((j) => `${j.job_number || j.jobNumber || '—'} — ${j.well_name || j.wellName || 'No Well'}`).join(', '),
    action: 'Open Dispatch',
    path: '/dispatch',
    urgent: true,
  });

  const leftOnSite = i.equipment.filter((e) => e.status === 'left_on_site');
  if (leftOnSite.length > 0) alerts.push({
    key: 'equipment-left-on-site',
    label: `${leftOnSite.length} equipment left on site`,
    detail: leftOnSite.slice(0, 2).map((e) => e.name || e.serial_number).join(', '),
    action: 'Schedule Pickup',
    path: '/equipment?status=left_on_site',
  });

  const equipCertsAlert = i.equipCerts.filter((c) => (c.percentUsed ?? 0) >= 90);
  if (equipCertsAlert.length > 0) alerts.push({
    key: 'equipment-certs',
    label: `${equipCertsAlert.length} equipment ${plural(equipCertsAlert.length, 'cert')} expiring soon`,
    detail: equipCertsAlert.slice(0, 2).map((c) => c.certDescription || c.name || c.serialNumber || 'Equipment').join(', '),
    action: 'View Certs',
    path: '/equipment/certs',
  });

  // Crew certs: TWO cards, never one summed figure. This read "Safety 47 certs
  // need attention" over 44 expired and 3 expiring, adding a person who cannot
  // be sent out today to a renewal someone should book this month. fe#757
  // settled the split on the Safety band: EXPIRED leads, expiring keeps its own
  // line. The counts only gate whether a card is drawn; an absent count draws
  // none, and a failed read is reported by the failure card above.
  const teamExpired = parseCount(i.teamCertSummary?.expired) ?? 0;
  const teamExpiring = parseCount(i.teamCertSummary?.expiringSoon) ?? 0;
  if (teamExpired > 0) alerts.push({
    key: 'team-certs-expired',
    label: `${teamExpired} crew ${plural(teamExpired, 'cert')} expired`,
    detail: 'Those people can’t be sent out until they renew',
    action: 'Review',
    path: '/safety/team-certs?status=expired',
  });
  if (teamExpiring > 0) alerts.push({
    key: 'team-certs-expiring',
    label: `${teamExpiring} crew ${plural(teamExpiring, 'cert')} expiring within 30 days`,
    detail: 'Renewals to book',
    action: 'Review',
    path: '/safety/team-certs?status=expiring',
  });

  const inspectionOverdue = i.equipment.filter((e) => {
    const raw = e.next_inspection_date || e.nextInspectionDate;
    if (!raw || i.today === null) return false;
    // An INSTANT (api#838) — the tenant's day it falls on, not its UTC prefix.
    const d = instantTenantDay(raw, i.timeZone);
    return !!d && d < i.today;
  });
  if (inspectionOverdue.length > 0) alerts.push({
    key: 'inspections-overdue',
    label: `${inspectionOverdue.length} ${plural(inspectionOverdue.length, 'inspection')} overdue`,
    detail: inspectionOverdue.slice(0, 2).map((e) => e.name || e.serial_number).join(', '),
    action: 'Schedule',
    path: '/equipment',
  });

  // Overdue invoices. A failed read is a card, never an omission.
  //
  // THE AMOUNT IS NOW THE OVERDUE AMOUNT. The old page printed
  // `balance_outstanding` (sent + overdue) under "N invoices overdue" and said
  // so in its detail, because no key summed overdue alone. api#809 added
  // `balance_overdue` — same status set as `overdue_count` — so the line now
  // reads the figure its heading names.
  if (f.invoices) {
    const { heading } = loadFailureMessage(f.invoices, 'overdue invoices');
    alerts.push({
      key: 'failure:invoices',
      label: heading,
      detail: f.invoices === 'refused'
        ? 'Not a statement about whether any are overdue'
        : 'Not a statement that none are overdue. Try again in a moment',
      action: 'Invoices',
      path: '/invoices',
      failure: f.invoices,
    });
  }
  const overdueInvoices = parseCount(i.invoiceDash?.overdue_count ?? i.invoiceDash?.overdueCount) ?? 0;
  if (overdueInvoices > 0) {
    const overdue = parseMoney(i.invoiceDash.balance_overdue ?? i.invoiceDash.balanceOverdue);
    alerts.push({
      key: 'invoices-overdue',
      label: `${overdueInvoices} ${plural(overdueInvoices, 'invoice')} overdue`,
      detail: overdue !== null && overdue > 0 ? `${fmtCurrency(overdue)} overdue` : '',
      action: 'View',
      path: '/invoices?status=overdue',
    });
  }

  if (f.quotes) {
    const { heading } = loadFailureMessage(f.quotes, 'quotes');
    alerts.push({
      key: 'failure:quotes',
      label: heading,
      detail: f.quotes === 'refused'
        ? 'Not a statement about whether any need follow-up'
        : 'Not a statement that none need follow-up. Try again in a moment',
      action: 'Quotes',
      path: '/quotes',
      failure: f.quotes,
    });
  }
  const sentQuotes = parseCount(i.quoteDash?.sent_count ?? i.quoteDash?.sentCount) ?? 0;
  const expiredQuotes = parseCount(i.quoteDash?.expired_count ?? i.quoteDash?.expiredCount) ?? 0;
  if (expiredQuotes > 0) {
    alerts.push({
      key: 'quotes-expired',
      label: `${expiredQuotes} ${plural(expiredQuotes, 'quote')} expired`,
      detail: 'Need follow-up or renewal',
      action: 'View',
      path: '/quotes',
    });
  } else if (sentQuotes > 0) {
    const pendingVal = parseMoney(i.quoteDash.pending_value ?? i.quoteDash.pendingValue);
    alerts.push({
      key: 'quotes-sent',
      label: `${sentQuotes} ${plural(sentQuotes, 'quote')} awaiting response`,
      detail: pendingVal !== null && pendingVal > 0 ? `${fmtCurrency(pendingVal, i.quoteCurrency ?? null)} pending` : '',
      action: 'Follow Up',
      path: '/quotes',
    });
  }

  // Tickets awaiting approval for more than three days.
  const now = i.now ?? Date.now();
  const stale = i.tickets.filter((t) => {
    if (t.status !== 'field_approved') return false;
    const at = t.updated_at || t.updatedAt || t.created_at || t.createdAt;
    if (!at) return false;
    const ms = Date.parse(at);
    return Number.isFinite(ms) && (now - ms) / 86_400_000 > 3;
  });
  if (stale.length > 0) alerts.push({
    key: 'tickets-stale',
    label: `${stale.length} ${plural(stale.length, 'ticket')} awaiting approval`,
    detail: `Submitted ${stale.length > 1 ? '3+ days ago' : 'over 3 days ago'}`,
    action: 'Review',
    path: '/tickets?status=field_approved',
  });

  // Customer-rejected tickets.
  //
  // THE KEYS ARE `total_value` AND `items`. The old page read `.total` and
  // `.tickets`, which GET /tickets/summary/dashboard has never sent, so the
  // blocked amount never printed and a single rejection never linked straight
  // to its ticket.
  //
  // `total_value` is the API's `sum + (it.total || 0)`, so an UNPRICED
  // rejected ticket adds nothing and the figure reads low. When any item has a
  // null total the amount is dropped rather than printed short — absent is not
  // zero, and a partial sum under "blocked" reads as the whole.
  const rejected = i.ticketDash?.customer_rejected_tickets ?? i.ticketDash?.customerRejectedTickets ?? {};
  const rejectedItems: any[] = Array.isArray(rejected.items) ? rejected.items : [];
  const rejectedCount = parseCount(rejected.count) ?? 0;
  if (rejectedCount > 0) {
    const anyUnpriced = rejectedItems.some((it) => parseMoney(it?.total) === null);
    const blocked = anyUnpriced ? null : parseMoney(rejected.total_value);
    const firstId = rejectedItems[0]?.id;
    alerts.push({
      key: 'tickets-customer-rejected',
      label: `${rejectedCount} ${plural(rejectedCount, 'ticket')} rejected by customer`,
      detail: blocked !== null && blocked > 0 ? `${fmtCurrency(blocked)} blocked` : '',
      action: 'Review',
      path: rejectedCount === 1 && firstId ? `/tickets/${firstId}` : '/tickets?status=customer_rejected',
      urgent: true,
    });
  }

  const pendingJsa = parseCount(i.safetyDash?.pendingJsaApprovals) ?? 0;
  if (pendingJsa > 0) alerts.push({
    key: 'jsa-pending',
    label: `${pendingJsa} ${plural(pendingJsa, 'JSA')} pending approval`,
    detail: 'Job Safety Analysis needs review',
    action: 'Review',
    path: '/safety/records?tab=jsas',
  });

  const openIncidents = parseCount(i.safetyDash?.openIncidents) ?? 0;
  if (openIncidents > 0) alerts.push({
    key: 'incidents-open',
    label: `${openIncidents} open ${plural(openIncidents, 'incident')} unresolved`,
    detail: 'Needs investigation or closure',
    action: 'View',
    path: '/safety/records?tab=incidents',
  });

  const seen = new Set<string>();
  return alerts.filter((a) => (seen.has(a.key) ? false : (seen.add(a.key), true)));
}
